import { Controls } from 'lib-analytics-types';
import { useCallback, useEffect } from 'react';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

export type IntelligenceAnalyticsEvents = {
  /** Pin an intelligence graph to Analytics. */
  'analytics:pinGraph': (arg: {
    /** The title of the intelligence panel. */
    readonly title: string;
    /** The controls for the intelligence panel used to request instruments. */
    readonly controls: Controls;
    /** The id of the dashboard associated with the intelligence panel. */
    readonly dashboardId?: string;
  }) => void;
};

export const useIntelligenceAnalyticsEvents = (): void => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const [createPanel] = analyticsApi.useCreatePanelMutation();
  const [deletePanel] = analyticsApi.useDeletePanelMutation();

  const handlePinGraphEvent: IntelligenceAnalyticsEvents['analytics:pinGraph'] =
    useCallback(
      async (arg) => {
        try {
          const dashboardId =
            arg.dashboardId ?? '1490c961-1553-4a57-bb5e-931a1b72c2e8';

          // First create the placeholder panel where the Intelligence graph will render. A panel
          // with a unique ID and reference to a dashboard must exist to be able to actually
          // associate it with the quick view selected in the next step.
          const {
            data: { panel },
          } = await createPanel({
            panel: {
              dashboard_id: dashboardId,
              title: arg.title,
              layout: { x: 0, y: 0, w: 24, h: 10 },
              comparable: true,
              exportable: true,
              has_in_depth: false,
            },
          }).unwrap();

          // When a panel is created, we can show the regular pin panel modal as we do on all
          // Analytics dashboards, where the user can select a quick view or create a new one.
          if (panel) {
            try {
              await dispatch(
                appActions.showModal({
                  modal: {
                    type: 'analytics:pinPanel',
                    arg: {
                      panel: {
                        ...panel,
                        dashboard_id: dashboardId,
                        controls: arg.controls,
                      },
                    },
                  },
                }),
              );
            } catch (_error) {
              // If the user chooses to abort the pinning process, we delete the placeholder panel
              // that was created since it is no longer needed.
              await deletePanel({ id: panel.id, dashboardId });
            }
          }
        } catch (error) {
          notify(
            {
              title:
                'We could not pin the Intelligence graph. Try again later.',
            },
            NotificationType.Error,
          );
        }
      },
      [createPanel, deletePanel, dispatch, notify],
    );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const { appEvents } = window._intelligence;

    appEvents.on('analytics:pinGraph', handlePinGraphEvent);
  }, [handlePinGraphEvent]);
};
